import React, { useState, useEffect } from "react"
import { DarkModeContext, type DarkModeContextType } from "./DarkModeContext"

const THEME_SWITCHING_CLASS = "theme-switching"

/**
 * Props accepted by the DarkModeProvider component.
 */
interface DarkModeProviderProps {
  children: React.ReactNode
}

/**
 * Provider that manages dark mode preference and syncs it to local storage and the DOM.
 *
 * @param children - Nested React nodes that should consume dark mode state.
 * @returns Context provider with dark mode value and toggle handler.
 */
export const DarkModeProvider: React.FC<DarkModeProviderProps> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage first, then system preference
    const savedMode = localStorage.getItem("darkMode")
    if (savedMode !== null) {
      return savedMode === "true"
    }
    return globalThis.matchMedia("(prefers-color-scheme: dark)").matches
  })

  useEffect(() => {
    // Update localStorage when dark mode changes
    localStorage.setItem("darkMode", isDarkMode.toString())

    // Disable transitions/animations briefly so the theme swap is instantaneous.
    document.documentElement.classList.add(THEME_SWITCHING_CLASS)

    // Update the HTML class for Tailwind dark mode
    document.documentElement.classList.toggle("dark", isDarkMode)

    // Keep the theme-switching class on for ~50ms to ensure the color change
    // happens without any transitions, then remove it to re-enable normal transitions.
    const timerId = globalThis.setTimeout(() => {
      document.documentElement.classList.remove(THEME_SWITCHING_CLASS)
    }, 50)

    return () => {
      globalThis.clearTimeout(timerId)
      document.documentElement.classList.remove(THEME_SWITCHING_CLASS)
    }
  }, [isDarkMode])

  const toggleDarkMode = () => {
    setIsDarkMode((prev) => !prev)
  }

  const value: DarkModeContextType = { isDarkMode, toggleDarkMode }

  return <DarkModeContext.Provider value={value}>{children}</DarkModeContext.Provider>
}
